import { useState, useEffect } from 'react';
import api from '../services/api';
import LogoFull from './LogoFull.jpg';
import {
  LuEye, LuEyeOff, LuShield, LuUser,
} from 'react-icons/lu';
import GoogleSignInButton, { isGoogleConfigured } from './GoogleSignInButton';
import AuthLayout from './AuthLayout';

const Login = ({ onLogin, onCreateAccount }) => {
  const [step, setStep] = useState('credentials'); // 'credentials' | '2fa'
  const [form, setForm] = useState({ username: '', password: '' });
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [googleOn, setGoogleOn] = useState(false);
  const [signupHint, setSignupHint] = useState(false);

  useEffect(() => { isGoogleConfigured().then(setGoogleOn); }, []);

  // Login com Google: o backend valida o token; conta inexistente → sugere criar conta.
  const handleGoogle = async (credential) => {
    setError(''); setSignupHint(false); setIsLoading(true);
    try {
      const { data } = await api.post('/auth/google', { credential });
      if (data.requiresTwoFactor) {
        setTempToken(data.tempToken);
        setStep('2fa');
      } else {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        onLogin(data.user);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro no login com Google');
      if (err.response?.data?.code === 'SIGNUP_REQUIRED') setSignupHint(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCredentials = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const { data } = await api.post('/auth/login', {
        username: form.username,
        password: form.password,
      });
      if (data.requiresTwoFactor) {
        setTempToken(data.tempToken);
        setStep('2fa');
      } else {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        onLogin(data.user);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao conectar ao servidor');
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FA = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const { data } = await api.post('/auth/verify-2fa', {
        tempToken,
        code: twoFactorCode,
      });
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      onLogin(data.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Código inválido');
      setTwoFactorCode('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout onCreateAccount={onCreateAccount}>
      <div style={styles.card}>

        <div className="auth-card-logo">
          <img src={LogoFull} alt="DHR Consultoria" />
        </div>

        <div style={styles.divider} />

        {step === 'credentials' && (
          <form onSubmit={handleCredentials} style={styles.form}>
            <p style={styles.formTitle}>
              <LuUser style={{ marginRight: 6 }} />
              Acesso ao Sistema
            </p>

            {error && (
              <div style={styles.errorBox}>
                {error}
                {signupHint && onCreateAccount && (
                  <button type="button" style={styles.inlineLink} onClick={onCreateAccount}>Criar minha conta</button>
                )}
              </div>
            )}

            <div style={styles.field}>
              <label style={styles.label}>Usuário ou e-mail</label>
              <input
                style={styles.input}
                type="text"
                placeholder="Digite seu usuário ou e-mail"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                autoComplete="username"
                required
                autoFocus
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Senha</label>
              <div style={styles.inputWrapper}>
                <input
                  style={{ ...styles.input, paddingRight: 42 }}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Digite sua senha"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  style={styles.eyeBtn}
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                >
                  {showPassword ? <LuEyeOff /> : <LuEye />}
                </button>
              </div>
            </div>

            <button
              style={{ ...styles.submitBtn, opacity: isLoading ? 0.75 : 1 }}
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? 'Entrando...' : 'Entrar'}
            </button>

            {googleOn && (
              <>
                <div style={styles.orDivider}><span style={styles.orLine} /><span>ou</span><span style={styles.orLine} /></div>
                <GoogleSignInButton mode="signin" onCredential={handleGoogle} />
              </>
            )}

            {onCreateAccount && (
              <div style={styles.signupBox}>
                <span style={styles.signupBoxText}>Novo por aqui? Comece grátis, sem cartão de crédito.</span>
                <button type="button" style={styles.signupBoxBtn} onClick={onCreateAccount}>Criar conta grátis</button>
              </div>
            )}
          </form>
        )}

        {step === '2fa' && (
          <form onSubmit={handle2FA} style={styles.form}>
            <div style={styles.twoFaHeader}>
              <LuShield style={styles.shieldIcon} />
              <p style={styles.formTitle}>Autenticação em Dois Fatores</p>
              <p style={styles.twoFaHint}>
                Digite o código de 6 dígitos gerado pelo seu aplicativo autenticador.
              </p>
            </div>

            {error && <div style={styles.errorBox}>{error}</div>}

            <div style={styles.field}>
              <label style={styles.label}>Código 2FA</label>
              <input
                style={{ ...styles.input, textAlign: 'center', fontSize: 24, letterSpacing: 10, fontWeight: 700 }}
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                required
                autoFocus
              />
            </div>

            <button
              style={{ ...styles.submitBtn, opacity: (isLoading || twoFactorCode.length !== 6) ? 0.6 : 1 }}
              type="submit"
              disabled={isLoading || twoFactorCode.length !== 6}
            >
              {isLoading ? 'Verificando...' : 'Verificar'}
            </button>

            <button
              type="button"
              style={styles.backBtn}
              onClick={() => { setStep('credentials'); setError(''); setTwoFactorCode(''); }}
            >
              ← Voltar ao login
            </button>
          </form>
        )}

        <div style={styles.footer}>
          © {new Date().getFullYear()} DHR Consultoria — Todos os direitos reservados
        </div>
      </div>
    </AuthLayout>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, var(--surface-3) 0%, var(--surface-3) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    background: 'var(--surface-1)',
    borderRadius: 16,
    width: '100%',
    maxWidth: 400,
    boxShadow: 'var(--shadow-md)',
    border: '1px solid var(--border)',
    overflow: 'hidden',
  },
  cardHeader: {
    background: 'var(--surface-3)',
    padding: '24px 24px 22px',
    textAlign: 'center',
  },
  logo: {
    width: '100%',
    maxWidth: 260,
    height: 'auto',
    borderRadius: 12,
    display: 'block',
    margin: '0 auto 16px',
  },
  title: {
    color: 'var(--accent)',
    fontSize: 22,
    fontWeight: 800,
    margin: 0,
    letterSpacing: -0.3,
  },
  subtitle: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 12,
    margin: '6px 0 0',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  divider: {
    height: 3,
    background: 'var(--accent)',
  },
  form: {
    padding: '28px 32px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  formTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: 'var(--text-1)',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
  },
  twoFaHeader: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
  },
  shieldIcon: {
    fontSize: 40,
    color: 'var(--accent)',
    marginBottom: 4,
  },
  twoFaHint: {
    fontSize: 13,
    color: 'var(--text-3)',
    margin: 0,
    lineHeight: 1.5,
  },
  errorBox: {
    background: 'var(--danger-soft)',
    border: '1px solid var(--danger-border)',
    color: 'var(--danger)',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    fontWeight: 500,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 12,
    fontWeight: 700,
    color: 'var(--text-2)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  inputWrapper: {
    position: 'relative',
  },
  input: {
    width: '100%',
    border: '1px solid var(--border-strong)',
    background: 'var(--surface-2)',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 14,
    color: 'var(--text-1)',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  eyeBtn: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--text-3)',
    fontSize: 16,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
  },
  submitBtn: {
    background: 'var(--accent)',
    border: 'none',
    borderRadius: 8,
    padding: '12px',
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--on-accent)',
    cursor: 'pointer',
    marginTop: 4,
    fontFamily: 'inherit',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--text-3)',
    fontSize: 13,
    cursor: 'pointer',
    textAlign: 'center',
    padding: 0,
    fontFamily: 'inherit',
  },
  footer: {
    padding: '14px 32px',
    borderTop: '1px solid var(--border)',
    fontSize: 11,
    color: 'var(--text-3)',
    textAlign: 'center',
  },
  orDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    color: 'var(--text-3)',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  orLine: {
    flex: 1,
    height: 1,
    background: 'var(--surface-3)',
  },
  switchText: {
    margin: '2px 0 0',
    textAlign: 'center',
    fontSize: 13,
    color: 'var(--text-2)',
  },
  switchLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    color: 'var(--text-1)',
    fontWeight: 700,
    textDecoration: 'underline',
    textDecorationColor: 'var(--accent)',
    textUnderlineOffset: 3,
    cursor: 'pointer',
    fontSize: 13,
    fontFamily: 'inherit',
  },
  signupBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
    paddingTop: 16,
    borderTop: '1px solid var(--border)',
    textAlign: 'center',
  },
  signupBoxText: {
    fontSize: 13,
    color: 'var(--text-2)',
  },
  signupBoxBtn: {
    width: '100%',
    background: 'var(--surface-1)',
    border: '1.5px solid var(--border-strong)',
    borderRadius: 8,
    padding: '11px',
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--text-1)',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  inlineLink: {
    display: 'block',
    marginTop: 6,
    background: 'none',
    border: 'none',
    padding: 0,
    color: 'var(--danger)',
    fontWeight: 700,
    textDecoration: 'underline',
    cursor: 'pointer',
    fontSize: 13,
    fontFamily: 'inherit',
  },
};

// Reaproveitado pela tela de cadastro (SignupPage.js) — mesma identidade visual.
export const authStyles = styles;

export default Login;
